
import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar, UserX, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SuspensionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  onSuspensionComplete: () => void;
  companyId?: string | null;
}

type SuspensionType = 'permanent' | 'temporary';

const SuspensionDialog: React.FC<SuspensionDialogProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName,
  onSuspensionComplete,
  companyId
}) => {
  const { user, profile } = useAuth();
  const resolvedCompanyId = companyId ?? profile?.company_id ?? null;
  const [suspensionType, setSuspensionType] = useState<SuspensionType>('temporary');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('7');
  const [isLoading, setIsLoading] = useState(false);
  const [reasonError, setReasonError] = useState('');
  const [daysError, setDaysError] = useState('');

  // Persist form state when dialog opens/closes but don't reset on tab switches
  useEffect(() => {
    if (isOpen && !reason && !reasonError && !daysError) {
      // Only reset if the form is completely clean (new dialog session)
      setSuspensionType('temporary');
      setDays('7');
    }
  }, [isOpen, reason, reasonError, daysError]);

  // Real-time validation for reason field
  const validateReason = useCallback((value: string) => {
    if (!value.trim()) {
      setReasonError('A reason for suspension is required');
      return false;
    }
    setReasonError('');
    return true;
  }, []);

  // Real-time validation for days field
  const validateDays = useCallback((value: string) => {
    if (suspensionType === 'temporary') {
      const numDays = parseInt(value);
      if (!value || numDays <= 0 || numDays > 365) {
        setDaysError('Please enter a valid number of days (1-365)');
        return false;
      }
    }
    setDaysError('');
    return true;
  }, [suspensionType]);

  const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setReason(value);
    validateReason(value);
  }, [validateReason]);

  const handleDaysChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDays(value);
    validateDays(value);
  }, [validateDays]);

  const handleSuspensionTypeChange = useCallback((value: SuspensionType) => {
    setSuspensionType(value);
    // Clear days error when switching to permanent
    if (value === 'permanent') {
      setDaysError('');
    } else {
      validateDays(days);
    }
  }, [days, validateDays]);

  const handleSuspend = useCallback(async () => {
    // Validate all fields before submission
    const isReasonValid = validateReason(reason);
    const isDaysValid = validateDays(days);

    if (!isReasonValid || !isDaysValid) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    if (!user?.id) {
      toast.error('You must be logged in to perform this action');
      return;
    }

    setIsLoading(true);
    try {
      let suspendedUntil = null;
      
      if (suspensionType === 'temporary') {
        const suspensionEndDate = new Date();
        suspensionEndDate.setDate(suspensionEndDate.getDate() + parseInt(days));
        suspendedUntil = suspensionEndDate.toISOString();
      }

      console.log('Attempting to suspend user:', {
        memberId,
        suspendedUntil,
        reason: reason.trim(),
        suspendedBy: user.id
      });

      const { data, error } = await supabase
        .rpc('suspend_member' as any, {
          p_user_id: memberId,
          p_company_id: resolvedCompanyId,
          p_reason: reason.trim() || null,
          p_suspended_until: suspendedUntil || null
        } as any);

      if (error) {
        console.error('Suspension error:', error);
        throw error;
      }

      toast.success(`${memberName} has been suspended successfully`);
      onSuspensionComplete();
      handleClose();
    } catch (error: any) {
      console.error('Error suspending user:', error);
      toast.error(`Failed to suspend user: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [reason, days, suspensionType, user?.id, memberId, memberName, onSuspensionComplete, validateReason, validateDays]);

  const handleClose = useCallback(() => {
    // Only reset form when explicitly closing the dialog
    setReason('');
    setDays('7');
    setSuspensionType('temporary');
    setReasonError('');
    setDaysError('');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-red-500" />
            Suspend User
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Suspending <strong>{memberName}</strong> will prevent them from accessing their account.
            </p>
          </div>

          <div className="space-y-3">
            <Label>Suspension Type</Label>
            <RadioGroup 
              value={suspensionType} 
              onValueChange={handleSuspensionTypeChange}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="temporary" id="temporary" />
                <Label htmlFor="temporary" className="flex items-center gap-2 cursor-pointer">
                  <Clock className="h-4 w-4" />
                  Temporary suspension
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="permanent" id="permanent" />
                <Label htmlFor="permanent" className="flex items-center gap-2 cursor-pointer">
                  <UserX className="h-4 w-4" />
                  Permanent suspension
                </Label>
              </div>
            </RadioGroup>
          </div>

          {suspensionType === 'temporary' && (
            <div className="space-y-2">
              <Label htmlFor="days">Number of days *</Label>
              <Input
                id="days"
                type="number"
                min="1"
                max="365"
                value={days}
                onChange={handleDaysChange}
                placeholder="Enter number of days"
                className={daysError ? 'border-red-500' : ''}
              />
              {daysError && (
                <div className="flex items-center gap-1 text-sm text-red-500">
                  <AlertTriangle className="h-3 w-3" />
                  {daysError}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">
              Reason for suspension <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={handleReasonChange}
              placeholder="Explain why this user is being suspended..."
              rows={3}
              className={reasonError ? 'border-red-500' : ''}
            />
            {reasonError && (
              <div className="flex items-center gap-1 text-sm text-red-500">
                <AlertTriangle className="h-3 w-3" />
                {reasonError}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSuspend} 
            disabled={isLoading || !!reasonError || !!daysError}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? 'Suspending...' : 'Suspend User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuspensionDialog;
